import moment from "moment";
import Utils from "../../../core/utilities/Utils";

/** Backend/API date format — do not use for UI display. */
export const ANOMALY_API_DATE_FORMAT = "YYYY-MM-DD";

/** User-facing date format across anomaly pages. */
export const ANOMALY_UI_DATE_FORMAT = "DD-MM-YYYY";

export const ANOMALY_UI_DATETIME_FORMAT = "DD-MM-YYYY HH:mm";

/** react-datepicker display format. */
export const ANOMALY_DATEPICKER_FORMAT = "dd-MM-yyyy";

const ANOMALY_PARSE_FORMATS = [moment.ISO_8601, ANOMALY_API_DATE_FORMAT, "YYYY-MM"];

export const formatAnomalyApiDate = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format(ANOMALY_API_DATE_FORMAT) : null;
};

export const formatAnomalyDisplayDate = (value) => {
  if (value == null || value === "") return "—";
  const m = moment(value, ANOMALY_PARSE_FORMATS, true);
  if (!m.isValid()) return String(value);
  if (/^\d{4}-\d{2}$/.test(String(value))) {
    return m.startOf("month").format(ANOMALY_UI_DATE_FORMAT);
  }
  return m.format(ANOMALY_UI_DATE_FORMAT);
};

export const formatAnomalyDisplayDateTime = (value) => {
  if (value == null || value === "") return "—";
  const m = moment(value);
  return m.isValid() ? m.format(ANOMALY_UI_DATETIME_FORMAT) : String(value);
};

/** Compact period label for tight UI cells (DD-MM). */
export const formatAnomalyDisplayPeriodShort = (value) => {
  if (!value) return "";
  const m = moment(value, ANOMALY_PARSE_FORMATS, true);
  return m.isValid() ? m.format("DD-MM") : String(value).slice(5);
};

/** Month + year label for anomaly heatmap cells (e.g. Jun 2026). */
export const formatAnomalyHeatmapPeriod = (value) => {
  if (!value) return "";
  const m = moment(value, ANOMALY_PARSE_FORMATS, true);
  return m.isValid() ? m.format("MMM YYYY") : String(value);
};

/** Last day of the previous calendar month — latest selectable anomaly period. */
export const getAnomalyMaxSelectableDate = () =>
  moment().subtract(1, "month").endOf("month").toDate();

/** Today — latest selectable resolved-date filter (anomalies can be resolved same day). */
export const getAnomalyResolvedDateMaxSelectableDate = () =>
  moment().endOf("day").toDate();

/** Default period end: last day of previous month. */
export const getAnomalyDefaultPeriodTo = () => getAnomalyMaxSelectableDate();

/** Default period start: first day of current year. */
export const getAnomalyDefaultPeriodFrom = () =>
  moment().startOf("year").toDate();

/** Default detection month: first day of previous month. */
export const getAnomalyDefaultDetectMonth = () =>
  moment().subtract(1, "month").startOf("month").toDate();

/** Clamp dates to the latest allowed anomaly period. */
export const clampAnomalyDate = (date) => {
  if (!date) return date;
  const max = getAnomalyMaxSelectableDate();
  return date > max ? max : date;
};

export const parseAnomalyPeriodDate = (value) => {
  if (!value) return null;
  return clampAnomalyDate(new Date(value));
};

export const getAnomalyPeriodFromMaxDate = (periodTo) => {
  const max = getAnomalyMaxSelectableDate();
  if (!periodTo) return max;
  return periodTo < max ? periodTo : max;
};

export const clampAnomalyResolvedDate = (date) => {
  if (!date) return date;
  const max = getAnomalyResolvedDateMaxSelectableDate();
  return date > max ? max : date;
};

export const parseAnomalyResolvedPeriodDate = (value) => {
  if (!value) return null;
  return clampAnomalyResolvedDate(new Date(value));
};

export const getAnomalyResolvedPeriodFromMaxDate = (periodTo) => {
  const max = getAnomalyResolvedDateMaxSelectableDate();
  if (!periodTo) return max;
  return periodTo < max ? periodTo : max;
};

export const onAnomalyPeriodChange = (setter) => (date) => {
  setter(date ? clampAnomalyDate(date) : date);
};

export const onAnomalyResolvedPeriodChange = (setter) => (date) => {
  setter(date ? clampAnomalyResolvedDate(date) : date);
};

export const normalizeAnomalyPeriodRange = (periodFrom, periodTo) => {
  const from = periodFrom ? clampAnomalyDate(periodFrom) : periodFrom;
  const to = periodTo ? clampAnomalyDate(periodTo) : periodTo;
  if (from && to && from > to) {
    return { periodFrom: to, periodTo: to };
  }
  return { periodFrom: from, periodTo: to };
};

/** Default list filter range: 1st through last day of previous calendar month. */
export const getAnomalyDefaultListPeriodRange = () =>
  normalizeAnomalyPeriodRange(
    getAnomalyDefaultDetectMonth(),
    getAnomalyDefaultPeriodTo()
  );

export const normalizeAnomalyResolvedPeriodRange = (periodFrom, periodTo) => {
  const from = periodFrom ? clampAnomalyResolvedDate(periodFrom) : periodFrom;
  const to = periodTo ? clampAnomalyResolvedDate(periodTo) : periodTo;
  if (from && to && from > to) {
    return { periodFrom: to, periodTo: to };
  }
  return { periodFrom: from, periodTo: to };
};

/** Default resolved-history filter range: 1st of previous month through today. */
export const getAnomalyDefaultResolvedPeriodRange = () =>
  normalizeAnomalyResolvedPeriodRange(
    getAnomalyDefaultDetectMonth(),
    getAnomalyResolvedDateMaxSelectableDate()
  );

export const resolveAnomalyPeriodState = (fromValue, toValue, defaultFrom, defaultTo) => {
  const hasFrom = Boolean(fromValue);
  const hasTo = Boolean(toValue);
  return normalizeAnomalyPeriodRange(
    hasFrom ? new Date(fromValue) : defaultFrom,
    hasTo ? new Date(toValue) : defaultTo
  );
};

/**
 * Caps home-page anomaly summary API range to the last day of the previous calendar month.
 * @returns {{ periodFrom: string, periodTo: string, isCapped: boolean, cappedFromDisplay: string, cappedThroughDisplay: string } | null}
 */
export const resolveAnomalySummaryHomePeriod = (periodStart, periodEnd) => {
  if (!periodStart || !periodEnd) return null;

  const maxDate = getAnomalyMaxSelectableDate();
  const maxAllowedStr = moment(maxDate).format(ANOMALY_API_DATE_FORMAT);
  const cappedThroughDisplay = moment(maxDate).format(ANOMALY_UI_DATE_FORMAT);
  const cappedFromDisplay = moment(periodStart).format(ANOMALY_UI_DATE_FORMAT);
  const isCapped = periodEnd > maxAllowedStr;
  const periodTo = isCapped ? maxAllowedStr : periodEnd;

  if (periodStart > periodTo) {
    return {
      periodFrom: periodStart,
      periodTo,
      isCapped: true,
      cappedFromDisplay,
      cappedThroughDisplay,
      isEntirelyFuture: true,
    };
  }

  return {
    periodFrom: periodStart,
    periodTo,
    isCapped,
    cappedFromDisplay,
    cappedThroughDisplay,
    isEntirelyFuture: false,
  };
};

/**
 * Compute periodFrom / periodTo Date objects from a structured frequency + period selection.
 *
 * @param {string} frequency - 'ANNUAL' | 'QUARTERLY' | 'MONTHLY' | 'CUSTOM'
 * @param {number|string} year - selected year (or "from" year for CUSTOM)
 * @param {string} month - "01"–"12"; for QUARTERLY this is the quarter-start month (Q1="01", Q2="04", Q3="07", Q4="10")
 * @param {number|string} toYear - "to" year (CUSTOM only)
 * @param {string} toMonth - "to" month "01"–"12" (CUSTOM only)
 * @returns {{ periodFrom: Date, periodTo: Date }}
 */
export const computeAnomalyPeriodRange = (frequency, year, month, toYear, toMonth) => {
  const y = String(year);

  if (frequency === 'ANNUAL') {
    return {
      periodFrom: new Date(`${y}-01-01`),
      periodTo: new Date(`${y}-12-31`),
    };
  }

  if (frequency === 'QUARTERLY') {
    const qStartMonth = month || '01';
    let qEndMonth;
    if (qStartMonth === '01') qEndMonth = '03';
    else if (qStartMonth === '04') qEndMonth = '06';
    else if (qStartMonth === '07') qEndMonth = '09';
    else qEndMonth = '12';
    const lastDay = Utils.getLastDayFor(`${y}-${qEndMonth}-01`);
    return {
      periodFrom: new Date(`${y}-${qStartMonth}-01`),
      periodTo: new Date(`${y}-${qEndMonth}-${String(lastDay).padStart(2, '0')}`),
    };
  }

  if (frequency === 'MONTHLY') {
    const m = month || '01';
    const lastDay = Utils.getLastDayFor(`${y}-${m}-01`);
    return {
      periodFrom: new Date(`${y}-${m}-01`),
      periodTo: new Date(`${y}-${m}-${String(lastDay).padStart(2, '0')}`),
    };
  }

  if (frequency === 'CUSTOM') {
    const fromY = String(year);
    const fromM = month || '01';
    const toY = String(toYear || year);
    const toM = toMonth || month || '01';
    const lastDay = Utils.getLastDayFor(`${toY}-${toM}-01`);
    return {
      periodFrom: new Date(`${fromY}-${fromM}-01`),
      periodTo: new Date(`${toY}-${toM}-${String(lastDay).padStart(2, '0')}`),
    };
  }

  return {
    periodFrom: getAnomalyDefaultPeriodFrom(),
    periodTo: getAnomalyDefaultPeriodTo(),
  };
};

/** Prefer displayPeriod (the user-facing affected period) over the raw evaluation `period`. */
export const getAnomalyDisplayPeriod = (record = {}) =>
  (record && (record.displayPeriod || record.period)) || null;

/** Format the preferred display period for a record (list cells, pills, etc). */
export const formatAnomalyRecordDisplayPeriod = (record) =>
  formatAnomalyDisplayDate(getAnomalyDisplayPeriod(record));

/** "MMMM YYYY" for titles and CTAs (e.g. "April 2024"). */
export const formatAnomalyMonthYear = (value) => {
  if (!value) return "";
  const m = moment(value, ANOMALY_PARSE_FORMATS, true);
  if (!m.isValid()) return String(value);
  return m.format("MMMM YYYY");
};
